"use client";

import { Html } from "@/Components";
import { CenterOptions } from "@/Helpers/CenterOptions";
import { useT } from "@/Hooks";
import { ChooseUsSectionProps } from "@/Templates/SectionsProps";
import { FC, ReactElement, ReactNode } from "react";
import HomeEighteenChooseSectionGenerated, {
  HomeEighteenChooseSectionGeneratedProps,
} from "../Sections/_generated/HomeEighteenChooseSection.generated";
import HomeElevenChooseusSectionGenerated, {
  HomeElevenChooseusSectionGeneratedProps,
} from "../Sections/_generated/HomeElevenChooseusSection.generated";
import HomeFifteenChooseUsSectionGenerated, {
  HomeFifteenChooseUsSectionGeneratedProps,
} from "../Sections/_generated/HomeFifteenChooseUsSection.generated";
import HomeFiveChooseSectionGenerated, {
  HomeFiveChooseSectionGeneratedProps,
} from "../Sections/_generated/HomeFiveChooseSection.generated";
import HomeFourteenChooseSectionGenerated, {
  HomeFourteenChooseSectionGeneratedProps,
} from "../Sections/_generated/HomeFourteenChooseSection.generated";
import HomeSeventeenChooseSectionGenerated, {
  HomeSeventeenChooseSectionGeneratedProps,
} from "../Sections/_generated/HomeSeventeenChooseSection.generated";
import HomeSixteenChooseUsSectionGenerated, {
  HomeSixteenChooseUsSectionGeneratedProps,
} from "../Sections/_generated/HomeSixteenChooseUsSection.generated";
import HomeTenChooseusSectionGenerated, {
  HomeTenChooseusSectionGeneratedProps,
} from "../Sections/_generated/HomeTenChooseusSection.generated";
import HomeThreeChooseSectionGenerated, {
  HomeThreeChooseSectionGeneratedProps,
} from "../Sections/_generated/HomeThreeChooseSection.generated";
import {
  buildGeneratedSectionProps,
  renderGeneratedSection,
} from "./generated-section-renderer";

type GeneratedChooseUsRenderer = (props: ChooseUsSectionProps) => ReactElement;

const ChooseUsAppearance =
  CenterOptions.SiteTemplate.SiteTemplateSettings
    .SiteTemplateChooseUsAppearance;

function renderHtmlValue(value?: string | null): ReactNode {
  if (!value) {
    return undefined;
  }

  return <Html value={value} />;
}

function getSectionImageUrl(props: ChooseUsSectionProps): string | undefined {
  return props.setting.Image?.Url ?? undefined;
}

function getSectionImageAlt(
  props: ChooseUsSectionProps,
  fallback: string,
): string {
  return props.setting.Image?.AltText ?? fallback;
}

function getListItems(props: ChooseUsSectionProps) {
  return props.lists.Default ?? [];
}

function getItemImageUrl(
  props: ChooseUsSectionProps,
  index: number,
): string | undefined {
  return getListItems(props)[index]?.Image?.Url ?? undefined;
}

function isImageLikeValue(value?: string | null): boolean {
  if (!value) {
    return false;
  }

  return (
    value.startsWith("/") ||
    value.startsWith("http://") ||
    value.startsWith("https://")
  );
}

function getItemIconSource(
  props: ChooseUsSectionProps,
  index: number,
  fallback?: string,
): string | undefined {
  const icon = getListItems(props)[index]?.Icon;
  if (isImageLikeValue(icon)) {
    return icon ?? undefined;
  }

  return getItemImageUrl(props, index) ?? fallback;
}

function getButtonVisible(props: ChooseUsSectionProps): boolean {
  return (
    props.setting.ButtonDisplayStatus.Key ===
    CenterOptions.Enum.Status.Active.key
  );
}

function getPrimaryPhone(props: ChooseUsSectionProps): string | undefined {
  return (
    props.pageData.CommonData.Tels?.find((tel) => Boolean(tel)) ?? undefined
  );
}

const generatedChooseUsRenderers: Record<string, GeneratedChooseUsRenderer> = {
  [ChooseUsAppearance.Home3.key]: (props) => {
    const items = getListItems(props);

    return (
      <HomeThreeChooseSectionGenerated
        {...buildGeneratedSectionProps<HomeThreeChooseSectionGeneratedProps>(
          "HomeThreeChooseSection",
          {
            sectionDiv0Div0Div0DivText: props.setting.Subtitle ?? undefined,
            sectionDiv0Div0H20H2Text: props.setting.Title ?? undefined,
            sectionDiv0Div0P0PText: renderHtmlValue(props.setting.Description),
            sectionDiv0Div1Div1Div0ImageWithBasePath0ImageSrc:
              getSectionImageUrl(props) ?? undefined,
            sectionDiv0Div1Div1Div0ImageWithBasePath0ImageAlt:
              getSectionImageAlt(props, "choose-img"),
            chooseSectionChooseThrees: items.slice(0, 3).map((item) => ({
              key: item.Id,
              h30Text: item.Title ?? "",
              p0Text: renderHtmlValue(item.Description),
            })),
            chooseSectionChooseThrees2: items.slice(3, 6).map((item) => ({
              key: item.Id,
              className: "choose-item-three",
              h30Text: item.Title ?? "",
              p0Text: renderHtmlValue(item.Description),
            })),
          },
        )}
      />
    );
  },
  [ChooseUsAppearance.Home5.key]: (props) => (
    <HomeFiveChooseSectionGenerated
      {...buildGeneratedSectionProps<HomeFiveChooseSectionGeneratedProps>(
        "HomeFiveChooseSection",
        {
          sectionDiv0Div0Div0DivText: props.setting.Subtitle ?? undefined,
          sectionDiv0Div0H20H2Text: props.setting.Title ?? undefined,
          sectionDiv0Div2H40H4Text: renderHtmlValue(props.setting.Description),
          sectionDiv0Div2H40Link0LinkHref:
            props.setting.ButtonLink ?? undefined,
          sectionDiv0Div2H40Link0LinkText:
            props.setting.ButtonText ?? undefined,
          sectionDiv0Div2H40Link0LinkVisible: getButtonVisible(props),
          sectionImageWithBasePath0ImageSrc:
            getSectionImageUrl(props) ?? undefined,
          sectionImageWithBasePath0ImageAlt: getSectionImageAlt(props, "star"),
          chooseSection3666s: getListItems(props).map((item, index) => ({
            key: item.Id,
            src:
              item.Image?.Url ??
              "/Templates/Doccure/img/about/choose-img-4.png",
            imageWithBasePath0Src:
              getItemIconSource(props, index) ??
              "/Templates/Doccure/img/about/choose-icon-1.svg",
            h30Text: item.Title ?? "",
            p0Text: renderHtmlValue(item.Description),
            imageWithBasePath0Src2:
              item.Image?.Url ??
              "/Templates/Doccure/img/about/choose-imgs-1.jpg",
          })),
        },
      )}
    />
  ),
  [ChooseUsAppearance.Home10.key]: (props) => (
    <HomeTenChooseusSectionGenerated
      {...buildGeneratedSectionProps<HomeTenChooseusSectionGeneratedProps>(
        "HomeTenChooseusSection",
        {
          sectionDiv0Div0Div0DivText: props.setting.Subtitle ?? undefined,
          sectionDiv0Div0H20H2Text: props.setting.Title ?? undefined,
          sectionImageWithBasePath0ImageSrc:
            getSectionImageUrl(props) ?? undefined,
          sectionImageWithBasePath0ImageAlt: getSectionImageAlt(props, "bg"),
          chooseusSection6FadeInUps: getListItems(props).map((item, index) => ({
            key: item.Id,
            src:
              getItemIconSource(props, index) ??
              "/Templates/Doccure/img/icons/choose-05.svg",
            h30Text: item.Title ?? "",
            p0Text: renderHtmlValue(item.Description),
          })),
        },
      )}
    />
  ),
  [ChooseUsAppearance.Home11.key]: (props) => (
    <HomeElevenChooseusSectionGenerated
      {...buildGeneratedSectionProps<HomeElevenChooseusSectionGeneratedProps>(
        "HomeElevenChooseusSection",
        {
          sectionDiv0Div0Div0DivText: props.setting.Subtitle ?? undefined,
          sectionDiv0Div0H20H2Text: props.setting.Title ?? undefined,
          sectionImageWithBasePath0ImageSrc:
            getSectionImageUrl(props) ?? undefined,
          sectionImageWithBasePath0ImageAlt: getSectionImageAlt(props, "icon"),
          chooseusSection336s: getListItems(props).map((item, index) => ({
            key: item.Id,
            dataWowDuration: `${index + 1}s`,
            src:
              item.Image?.Url ??
              "/Templates/Doccure/img/category/choose-img-1.jpg",
            link0Text: item.Title ?? "",
            p0Text: renderHtmlValue(item.Description),
            href: item.Link ?? "#",
            link0Href: item.Link ?? "#",
          })),
        },
      )}
    />
  ),
  [ChooseUsAppearance.Home14.key]: (props) => (
    <HomeFourteenChooseSectionGenerated
      {...buildGeneratedSectionProps<HomeFourteenChooseSectionGeneratedProps>(
        "HomeFourteenChooseSection",
        {
          sectionDiv0Div1Div0Div0H20H2Text: props.setting.Title ?? undefined,
          sectionDiv0Div1Div0Div0H20Span0SpanText:
            props.setting.Subtitle ?? undefined,
          sectionDiv0Div1Div0Div0H20Span1SpanText:
            props.setting.Description ?? undefined,
          sectionDiv0Div0ImageWithBasePath0ImageSrc:
            getSectionImageUrl(props) ?? undefined,
          sectionDiv0Div0ImageWithBasePath0ImageAlt: getSectionImageAlt(
            props,
            "bg",
          ),
          chooseSection6s: getListItems(props).map((item, index) => ({
            key: item.Id,
            className:
              index % 2 === 0
                ? "offer-item rotate-1 flex-fill"
                : "offer-item rotate-n1 flex-fill",
            src:
              getItemIconSource(props, index) ??
              "/Templates/Doccure/img/icons/offer-01.svg",
            h30Text: item.Title ?? "",
            p0Text: renderHtmlValue(item.Description),
          })),
        },
      )}
    />
  ),
  [ChooseUsAppearance.Home15.key]: (props) => (
    <HomeFifteenChooseUsSectionGenerated
      {...buildGeneratedSectionProps<HomeFifteenChooseUsSectionGeneratedProps>(
        "HomeFifteenChooseUsSection",
        {
          sectionDiv0Div0Div0Div0ImageWithBasePath0ImageSrc:
            getSectionImageUrl(props) ?? undefined,
          sectionDiv0Div0Div0Div0ImageWithBasePath0ImageAlt: getSectionImageAlt(
            props,
            "img",
          ),
          sectionDiv0Div0Div1Div0Div0Div0DivText:
            props.setting.Subtitle ?? undefined,
          sectionDiv0Div0Div1Div0Div0H20H2Text:
            props.setting.Title ?? undefined,
          sectionDiv0Div0Div1Div0Div0H20H2Text2: "",
          sectionDiv0Div0Div1Div0Div0H20Span0SpanText:
            props.setting.Description ?? undefined,
          chooseUsSectionChooseFifteenFadeInUps: getListItems(props).map(
            (item, index) => ({
              key: item.Id,
              src:
                getItemIconSource(props, index) ??
                "/Templates/Doccure/img/icons/choose-icon-5.svg",
              h30Text: item.Title ?? "",
              p0Text: renderHtmlValue(item.Description),
              className:
                index === 0 ? "choose-description" : "why-choose-description",
            }),
          ),
        },
      )}
    />
  ),
  [ChooseUsAppearance.Home16.key]: (props) => (
    <HomeSixteenChooseUsSectionGenerated
      {...buildGeneratedSectionProps<HomeSixteenChooseUsSectionGeneratedProps>(
        "HomeSixteenChooseUsSection",
        {
          sectionDiv0Div0Div0DivText: props.setting.Subtitle ?? undefined,
          sectionDiv0Div0H20H2Text: props.setting.Title ?? undefined,
          sectionDiv0Div0H20Span0SpanText:
            props.setting.Description ?? undefined,
          sectionDiv0Div0H20Span0SpanText2: "",
          sectionImageWithBasePath0ImageSrc:
            getSectionImageUrl(props) ?? undefined,
          sectionImageWithBasePath0ImageAlt: getSectionImageAlt(props, "img"),
          chooseUsSection46s: getListItems(props).map((item, index) => ({
            key: item.Id,
            dataWowDuration: `${1 + index * 0.5}s`,
            src:
              getItemIconSource(props, index) ??
              "/Templates/Doccure/img/bg/choose-img-6.png",
            h30Text: item.Title ?? "",
            p0Text: renderHtmlValue(item.Description),
          })),
        },
      )}
    />
  ),
  [ChooseUsAppearance.Home17.key]: (props) => (
    <HomeSeventeenChooseSectionGenerated
      {...buildGeneratedSectionProps<HomeSeventeenChooseSectionGeneratedProps>(
        "HomeSeventeenChooseSection",
        {
          sectionDiv0Div0Div0Div0ImageWithBasePath0ImageSrc:
            getSectionImageUrl(props) ?? undefined,
          sectionDiv0Div0Div0Div0ImageWithBasePath0ImageAlt: getSectionImageAlt(
            props,
            "Health",
          ),
          sectionDiv0Div0Div1Div0H20H2Text: props.setting.Title ?? undefined,
          sectionDiv0Div0Div1Div0P0PText: renderHtmlValue(
            props.setting.Description,
          ),
          sectionDiv0Div0Div1Div2A0AHref: props.setting.ButtonLink ?? undefined,
          sectionDiv0Div0Div1Div2A0Span0SpanText:
            props.setting.ButtonText ?? undefined,
          chooseSection64s: getListItems(props).map((item, index) => ({
            key: item.Id,
            src:
              getItemIconSource(props, index) ??
              "/Templates/Doccure/img/icons/choose-icon-01.svg",
            h30Text: item.Title ?? "",
            p0Text: renderHtmlValue(item.Description),
          })),
        },
      )}
    />
  ),
  [ChooseUsAppearance.Home18.key]: (props) => (
    <HomeEighteenChooseSectionGenerated
      {...buildGeneratedSectionProps<HomeEighteenChooseSectionGeneratedProps>(
        "HomeEighteenChooseSection",
        {
          sectionDiv0Div0Div0DivText: props.setting.Subtitle ?? undefined,
          sectionDiv0Div0H20H2Text: props.setting.Title ?? undefined,
          sectionDiv0Div0H20H2Text2: props.setting.Description ?? undefined,
          sectionDiv0Div0H20Span0ImageWithBasePath0ImageSrc:
            getSectionImageUrl(props) ?? undefined,
          sectionDiv0Div0H20Span0ImageWithBasePath0ImageAlt: getSectionImageAlt(
            props,
            "title",
          ),
          chooseSection336612s: getListItems(props).map((item, index) => ({
            key: item.Id,
            dataWowDuration: `${1 + index * 0.5}s`,
            src:
              getItemIconSource(props, index) ??
              "/Templates/Doccure/img/category/choose-icon-01.svg",
            h30Text: item.Title ?? "",
            span0Text: item.LinkText ?? "",
            p0Text: renderHtmlValue(item.Description),
            imageWithBasePath0Src:
              item.Image?.Url ??
              "/Templates/Doccure/img/category/choose-thumb-1.jpg",
            imageWithBasePath1Src:
              item.Image?.Url ??
              "/Templates/Doccure/img/category/choose-large-1.jpg",
          })),
        },
      )}
    />
  ),
};

const GeneratedChooseUsSection: FC<ChooseUsSectionProps> = (props) => {
  const t = useT();

  return renderGeneratedSection(
    props,
    {
      ...generatedChooseUsRenderers,
      [ChooseUsAppearance.Home14.key]: (sectionProps) => (
        <HomeFourteenChooseSectionGenerated
          {...buildGeneratedSectionProps<HomeFourteenChooseSectionGeneratedProps>(
            "HomeFourteenChooseSection",
            {
              sectionDiv0Div1Div0Div0H20H2Text:
                sectionProps.setting.Title ?? undefined,
              sectionDiv0Div1Div0Div0H20Span0SpanText:
                sectionProps.setting.Subtitle ?? undefined,
              sectionDiv0Div1Div0Div0H20Span1SpanText:
                sectionProps.setting.Description ?? undefined,
              sectionDiv0Div0ImageWithBasePath0ImageSrc:
                getSectionImageUrl(sectionProps) ?? undefined,
              sectionDiv0Div0ImageWithBasePath0ImageAlt: getSectionImageAlt(
                sectionProps,
                "bg",
              ),
              chooseSectionOfferInfos: [
                ...(sectionProps.pageData.CommonData.Email
                  ? [
                      {
                        key: "email",
                        className: "isax isax-messages-3",
                        p0Text: t(
                          "landing.footer.emailAddress",
                          undefined,
                          "Email",
                        ),
                        p1Text: sectionProps.pageData.CommonData.Email,
                      },
                    ]
                  : []),
                ...(getPrimaryPhone(sectionProps)
                  ? [
                      {
                        key: "phone",
                        className: "isax isax-call-calling",
                        p0Text: t("landing.footer.phone", undefined, "Phone"),
                        p1Text: getPrimaryPhone(sectionProps)!,
                      },
                    ]
                  : []),
              ],
              chooseSection6s: getListItems(sectionProps).map(
                (item, index) => ({
                  key: item.Id,
                  className:
                    index % 2 === 0
                      ? "offer-item rotate-1 flex-fill"
                      : "offer-item rotate-n1 flex-fill",
                  src:
                    getItemIconSource(sectionProps, index) ??
                    "/Templates/Doccure/img/icons/offer-01.svg",
                  h30Text: item.Title ?? "",
                  p0Text: renderHtmlValue(item.Description),
                }),
              ),
            },
          )}
        />
      ),
    },
    ChooseUsAppearance.Home3.key,
    (x) => x.setting.Appearance?.Key,
  );
};

export default GeneratedChooseUsSection;
